import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "react-qr-code";
import { Ticket, Pencil, Check, X, Share2, Radar, Sparkles, PiggyBank, Store, Copy, QrCode, RefreshCw, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { ensureAnonUser } from "@/lib/anonSession";
import CouponCard, { daysLeft, urgencyLabel } from "@/components/hola/CouponCard";
import InstallPrompt from "@/components/hola/InstallPrompt";
import PushOptIn from "@/components/hola/PushOptIn";
import GoldCard from "@/components/hola/GoldCard";
import { UserTabBar } from "@/pages/UserHubPage";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

export default function CuponeraPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("available");
  const [editName, setEditName] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(null);
  const [deviceId, setDeviceId] = useState(null);

  const load = async () => {
    try {
      const user = await ensureAnonUser();
      setDeviceId(user.device_id);
      const { data: d } = await api.get(`/pwa/cuponera/${user.device_id}`);
      setData(d);
      setName(d.user.display_name || "");
    } catch { toast.error("No pude cargar tu Cuponera"); }
  };
  useEffect(() => { load(); }, []);

  const saveName = async () => {
    try {
      await api.put(`/users/anon/${deviceId}/profile`, { display_name: name.trim() });
      setEditName(false);
      toast.success(name.trim() ? `¡Hola, ${name.trim()}!` : "Nombre eliminado");
      load();
    } catch { toast.error("No pude guardar"); }
  };

  const open = async (c) => {
    setSelected(c);
    if (!c.viewed_at && c.status === "available") {
      try { await api.post(`/pwa/cuponera/${deviceId}/view/${c.uc_id}`); } catch { /* silent */ }
      setData((d) => ({ ...d, summary: { ...d.summary, new: Math.max(0, d.summary.new - 1) }, coupons: d.coupons.map((x) => (x.uc_id === c.uc_id ? { ...x, viewed_at: new Date().toISOString() } : x)) }));
    }
  };

  const urgencyOf = (c) => { const n = daysLeft(c.valid_until); return n === null ? 999 : n; };
  const list = useMemo(() => (data?.coupons || []).filter((c) => (tab === "available" ? c.status === "available" : c.status !== "available")).sort((a, b) => urgencyOf(a) - urgencyOf(b)), [data, tab]);
  const expiring = useMemo(() => (data?.coupons || []).filter((c) => c.status === "available" && urgencyOf(c) <= 2).sort((a, b) => urgencyOf(a) - urgencyOf(b)), [data]);
  const grouped = useMemo(() => {
    const g = new Map();
    list.forEach((c) => { const k = c.merchant.merchant_id; if (!g.has(k)) g.set(k, { merchant: c.merchant, items: [] }); g.get(k).items.push(c); });
    return [...g.values()];
  }, [list]);

  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-[#0A0A0A] text-white"><div className="text-center"><Ticket className="w-10 h-10 mx-auto animate-pulse text-[#FF007A]" /><div className="font-heading font-extrabold mt-2">Abriendo tu Cuponera…</div></div></div>;
  }
  const s = data.summary;
  const greet = data.user.display_name ? `Hola, ${data.user.display_name}` : "Hola";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-x-hidden" data-testid="cuponera-page">
      <div className="absolute -top-40 right-0 w-96 h-96 rounded-full bg-[#FF007A]/25 blur-3xl pointer-events-none" />
      <div className="absolute top-96 -left-32 w-80 h-80 rounded-full bg-[#0055FF]/25 blur-3xl pointer-events-none" />

      <div className="relative max-w-md mx-auto px-5 pt-8 pb-28">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              {editName ? (
                <div className="flex items-center gap-1.5">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className="h-9 rounded-lg bg-white text-black border-0 w-40" maxLength={60} autoFocus data-testid="cuponera-name-input" />
                  <button onClick={saveName} className="w-9 h-9 rounded-lg bg-[#FF007A] grid place-items-center" data-testid="cuponera-name-save"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditName(false)} className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => setEditName(true)} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="cuponera-edit-name">
                  <span data-testid="cuponera-greeting">{greet}</span> <Pencil className="w-3 h-3" />
                  {!data.user.display_name && <span className="text-[10px] text-[#FF007A] font-bold">agrega tu nombre</span>}
                </button>
              )}
            </div>
            <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">
              CUPON<span className="text-[#FF007A]">ERA</span>
            </h1>
          </div>
          <button onClick={load} className="w-10 h-10 rounded-full bg-white/10 grid place-items-center shrink-0" aria-label="Actualizar" data-testid="cuponera-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>

        {/* Ahorro hero */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-[28px] bg-gradient-to-br from-[#FF007A] via-[#d0127f] to-[#0055FF] p-5 shadow-2xl shadow-[#FF007A]/30 relative overflow-hidden" data-testid="cuponera-savings">
          <PiggyBank className="absolute -right-4 -bottom-4 w-36 h-36 text-white/10" />
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">Ahorro disponible en tus cupones</div>
          <div className="font-heading font-black text-5xl leading-none mt-1" data-testid="cuponera-potential">{money(s.potential_savings)}</div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <Mini v={s.available} l="cupones listos" testid="cuponera-available" />
            <Mini v={money(s.realized_savings)} l="ya ahorraste" testid="cuponera-realized" />
            <Mini v={s.merchants} l="comercios" testid="cuponera-merchants" />
          </div>
          {s.new > 0 && <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-white text-[#FF007A] px-3 py-1 text-xs font-extrabold" data-testid="cuponera-new-badge"><Sparkles className="w-3 h-3" /> {s.new} cupón{s.new > 1 ? "es" : ""} nuevo{s.new > 1 ? "s" : ""}</div>}
        </motion.div>

        {/* Por vencer */}
        {expiring.length > 0 && (
          <motion.button initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} onClick={() => open(expiring[0])} className="mt-4 w-full text-left rounded-2xl bg-amber-400 text-black p-4 flex items-center gap-3 shadow-lg shadow-amber-400/30" data-testid="cuponera-expiring-strip">
            <div className="w-11 h-11 rounded-xl bg-black/10 grid place-items-center shrink-0"><Flame className="w-6 h-6 animate-pulse" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-black text-base leading-tight">{urgencyLabel(urgencyOf(expiring[0]))} — {expiring[0].discount_label} en {expiring[0].merchant.business_name}</div>
              <div className="text-xs font-semibold text-black/70 mt-0.5">{expiring.length === 1 ? "Úsalo antes de que se pierda. ¡Ve hoy!" : `${expiring.length} cupones están por vencer. ¡Aprovéchalos!`}</div>
            </div>
          </motion.button>
        )}

        <div className="mt-4"><GoldCard deviceId={deviceId} /></div>

        <div className="mt-4"><PushOptIn deviceId={deviceId} compact /></div>

        {/* Tabs */}
        <div className="mt-6 grid grid-cols-2 gap-1 rounded-full bg-white/10 p-1">
          {[["available", `Disponibles (${s.available})`], ["used", `Usados / vencidos (${data.coupons.length - s.available})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cuponera-tab-${k}`} className={`h-10 rounded-full text-xs font-bold transition-colors ${tab === k ? "bg-white text-black" : "text-white/70"}`}>{l}</button>
          ))}
        </div>

        {/* Lista */}
        {grouped.length === 0 ? (
          <div className="mt-8 rounded-3xl bg-white/5 border border-white/10 p-8 text-center" data-testid="cuponera-empty">
            <div className="w-16 h-16 rounded-2xl bg-[#FF007A]/15 text-[#FF007A] grid place-items-center mx-auto mb-3"><QrCode className="w-8 h-8" /></div>
            <div className="font-heading font-extrabold text-lg">{tab === "available" ? "Aún no tienes cupones" : "Nada por aquí todavía"}</div>
            <p className="text-sm text-white/60 mt-1">Escanea el QR de cupones de tu comercio favorito y recibe descuentos exclusivos aquí.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {grouped.map((g) => (
              <div key={g.merchant.merchant_id} data-testid={`cuponera-group-${g.merchant.merchant_id}`}>
                <div className="flex items-center gap-2 mb-3">
                  {g.merchant.logo_url ? <img src={g.merchant.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" /> : <div className="w-8 h-8 rounded-lg bg-white/10 grid place-items-center"><Store className="w-4 h-4" /></div>}
                  <div><div className="font-heading font-extrabold leading-tight">{g.merchant.business_name}</div><div className="text-[10px] uppercase tracking-widest text-white/50">{g.items.length} cupón{g.items.length > 1 ? "es" : ""}</div></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {g.items.map((c, i) => (
                    <motion.button key={c.uc_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} onClick={() => open(c)} className="text-left relative" data-testid={`cuponera-coupon-${c.uc_id}`}>
                      <CouponCard coupon={c} merchantName={c.merchant.business_name} compact status={c.status} holeColor="#0A0A0A" />
                      {!c.viewed_at && c.status === "available" && <span className="absolute top-2 right-2 w-3 h-3 rounded-full bg-emerald-400 ring-4 ring-emerald-400/30 animate-pulse" />}
                    </motion.button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10"><InstallPrompt variant="compact" /></div>
        <button onClick={() => navigate("/radar")} className="mt-4 w-full rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center gap-3 text-left hover:bg-white/10" data-testid="cuponera-go-radar">
          <div className="w-10 h-10 rounded-xl bg-[#0055FF] grid place-items-center"><Radar className="w-5 h-5" /></div>
          <div className="flex-1"><div className="font-bold text-sm">Ofertas cerca de mí</div><div className="text-xs text-white/60">Activa el radar GPS y descubre promos a 50 m</div></div>
        </button>
      </div>

      <AnimatePresence>{selected && <CouponDetail c={selected} onClose={() => setSelected(null)} />}</AnimatePresence>
      <UserTabBar active="cuponera" />
    </div>
  );
}

function Mini({ v, l, testid }) {
  return <div className="rounded-2xl bg-white/15 backdrop-blur py-2 px-1" data-testid={testid}><div className="font-heading font-black text-lg leading-none">{v}</div><div className="text-[9px] uppercase tracking-wider text-white/80 mt-1">{l}</div></div>;
}

function CouponDetail({ c, onClose }) {
  const share = async () => {
    const text = `🎟️ Tengo un cupón de ${c.discount_label} en ${c.merchant.business_name} (${c.title}). ¡Escanea su QR y consigue el tuyo! ${window.location.origin}/c/${c.merchant.merchant_id}`;
    if (navigator.share) { try { await navigator.share({ text }); return; } catch { /* cancelled */ } }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };
  const copy = async () => { try { await navigator.clipboard.writeText(c.code); toast.success("Código copiado"); } catch { toast.error("No se pudo copiar"); } };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="coupon-detail" onClick={onClose}>
      <div className="min-h-full flex items-end sm:items-center justify-center p-4">
        <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm">
          <CouponCard coupon={c} merchantName={c.merchant.business_name} code={c.status === "available" ? c.code : undefined} status={c.status} holeColor="#000000" testid="coupon-detail-card" />
          <div className="mt-4 rounded-3xl bg-white text-black p-5">
            {c.message && <p className="text-sm italic text-neutral-700 mb-3" data-testid="coupon-detail-message">“{c.message}”</p>}
            {c.status === "available" ? (
              <>
                <div className="flex items-center gap-4">
                  <div className="bg-white p-2 rounded-xl border border-neutral-200"><QRCode value={c.code} size={88} /></div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Cómo usarlo</div>
                    <ol className="text-xs text-neutral-700 mt-1 space-y-1 list-decimal list-inside">
                      <li>Ve a <strong>{c.merchant.business_name}</strong>{c.merchant.address ? ` (${c.merchant.address})` : ""}</li>
                      <li>Muestra este código en caja</li>
                      <li>El descuento se aplica al momento</li>
                    </ol>
                  </div>
                </div>
                {c.conditions && <p className="text-[11px] text-neutral-500 mt-3"><strong>Condiciones:</strong> {c.conditions}</p>}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button onClick={copy} variant="outline" className="rounded-full h-11 font-bold" data-testid="coupon-detail-copy"><Copy className="w-4 h-4" /> Copiar</Button>
                  <Button onClick={share} className="rounded-full h-11 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold" data-testid="coupon-detail-share"><Share2 className="w-4 h-4" /> Compartir</Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-neutral-700" data-testid="coupon-detail-used">
                {c.status === "redeemed" ? <>Canjeado el {new Date(c.redeemed_at).toLocaleDateString("es-MX")} · Ahorraste <strong>{money(c.discount_amount)}</strong> 🎉</> : "Este cupón ya venció. ¡Pronto llegarán nuevos!"}
              </div>
            )}
            <button onClick={onClose} className="mt-4 w-full text-center text-xs text-neutral-500 font-bold" data-testid="coupon-detail-close">Cerrar</button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
